import { type ApiError, isApiError } from '@emma/api_client/client';
import type { CustomUseToastOptions } from '@lego/klik-ui';
import { ZodError } from 'zod';
import { ToastOptions } from '@/utility/ToastOptions';
import { emmaTeamsChannelUrl } from './external_urls';

export class ToastFactory {
  public static fromError(error: unknown): CustomUseToastOptions {
    if (error instanceof ZodError) {
      return ToastFactory.handleApiClientError();
    } else if (isApiError(error)) {
      if (error.code === 'ECONNABORTED' || error.message === 'Request aborted') {
        // Silent ignore for aborted/timeout requests during navigation or refresh
        return { visible: false } as unknown as CustomUseToastOptions;
      }
      console.error('API error:', error);
      return ToastFactory.handleApiCallError(error);
    } else {
      console.error('Unknown error:', error);
      return ToastFactory.handleUnknownError();
    }
  }

  private static pleaseNotifyUsLink = (
    <a href={emmaTeamsChannelUrl}>Please notify the EMMA team.</a>
  );
  private static ifPersistsPleaseNotifyUsLink = (
    <a href={emmaTeamsChannelUrl}>If the issue persists, please notify the EMMA team.</a>
  );

  private static internalError = ToastOptions.error(
    'Internal Error',
    'An internal error has occured.',
    this.pleaseNotifyUsLink
  );

  private static unknownError = ToastOptions.error(
    'Unknown Error',
    'An unknown error has occured.',
    this.pleaseNotifyUsLink
  );

  private static handleApiClientError(): CustomUseToastOptions {
    return ToastFactory.internalError;
  }

  private static handleApiCallError(error: ApiError): CustomUseToastOptions {
    switch (error.response?.status) {
      case 400: {
        return ToastFactory.handleBadRequest(error);
      }
      case 401: {
        return ToastFactory.handleUnauthorized();
      }
      case 403: {
        return ToastFactory.handleForbidden(error);
      }
      case 404: {
        return ToastFactory.handleNotFoundError(error);
      }
      case 409: {
        return ToastFactory.handleConflict(error);
      }
      case 418: {
        return ToastFactory.handleImATeapot(error);
      }
      case 422: {
        return ToastFactory.handleUnprocessableEntity(error);
      }
      case 500: {
        return ToastFactory.handleInternalServerError();
      }
      case 502: {
        return ToastFactory.handleBadGateway(error);
      }
      default: {
        return ToastFactory.unknownError;
      }
    }
  }

  private static handleBadRequest(error: ApiError): CustomUseToastOptions {
    type ErrorData = { detail: unknown; status: number; title: string };
    const errInfo = (error.response?.data as ErrorData) ?? { title: '?' };
    return ToastOptions.error(
      `Bad Request - ${errInfo.title}`,
      'Please refresh.',
      ToastFactory.ifPersistsPleaseNotifyUsLink
    );
  }

  private static handleNotFoundError(error: ApiError): CustomUseToastOptions {
    type ErrorData = { detail?: string; status?: number; title?: string };
    const errInfo = (error.response?.data as ErrorData | undefined) ?? {};
    return ToastOptions.error(
      errInfo.title ?? 'Not found',
      `${errInfo.detail ?? 'The requested resource could not be found.'} - Please refresh.`,
      ToastFactory.ifPersistsPleaseNotifyUsLink
    );
  }

  private static handleUnauthorized(): CustomUseToastOptions {
    return ToastOptions.error('Unauthorized', 'You have not been authorized.');
  }

  private static handleForbidden(error: ApiError): CustomUseToastOptions {
    type ErrorData = { detail?: string; status?: number; title?: string };
    const errInfo = (error.response?.data as ErrorData | undefined) ?? {};
    return ToastOptions.error(
      errInfo.title ?? 'Forbidden',
      errInfo.detail ?? 'You do not have access to this resource.'
    );
  }

  private static handleConflict(error: ApiError): CustomUseToastOptions {
    type ErrorData = { detail?: string; status?: number; title?: string };
    const errInfo = (error.response?.data as ErrorData | undefined) ?? {};
    return ToastOptions.error(
      errInfo.title ?? 'Conflict',
      errInfo.detail ?? 'The request conflicts with the current state.'
    );
  }

  private static handleImATeapot(error: ApiError): CustomUseToastOptions {
    type ErrorData = { detail?: string; status?: number; title?: string };
    const errInfo = (error.response?.data as ErrorData | undefined) ?? {};
    return ToastOptions.error(errInfo.title ?? "I'm a teapot", errInfo.detail ?? '');
  }

  private static handleUnprocessableEntity(error: ApiError): CustomUseToastOptions {
    type ErrorData = { detail?: string; status?: number; title?: string };
    const errInfo = (error.response?.data as ErrorData | undefined) ?? {};
    const title = errInfo.title
      ? `Unprocessable request - ${errInfo.title}`
      : 'Unprocessable request';
    return ToastOptions.error(title, errInfo.detail ?? '');
  }

  private static handleInternalServerError(): CustomUseToastOptions {
    return ToastOptions.error(
      'Internal server error',
      'An internal error occurred in the API.',
      ToastFactory.pleaseNotifyUsLink
    );
  }

  private static handleBadGateway(error: ApiError): CustomUseToastOptions {
    type ErrorData = { detail?: string; status?: number; title?: string };
    const errInfo = (error.response?.data as ErrorData | undefined) ?? {};
    return ToastOptions.error(
      errInfo.title ?? 'Bad gateway',
      errInfo.detail ?? 'The server received an invalid response from an upstream service.'
    );
  }

  private static handleUnknownError(): CustomUseToastOptions {
    return ToastFactory.unknownError;
  }

  public static validationError(message: string): CustomUseToastOptions {
    return ToastOptions.error('Validation Error', message);
  }
}
